import { useState } from 'react';
import { EEthereumNetworkId } from '@ankr.com/provider';
import { ArrowDown } from '@ankr.com/ui';
import { TableCell, TableRow, useMediaQuery, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import BigNumber from 'bignumber.js';
import { makeStyles } from 'tss-react/mui';

import { cropString, useChainData } from 'modules/common';
import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';

import { GetTasksResult } from '../../actions/getTasks';
import { ExpandedTask } from './ExpandedTask';
import translation from './groupedTasksTable.translation';

interface Props {
  chainId: EEthereumNetworkId;
  address: string;
  tasks: GetTasksResult;
}

const mergedTranslation = mergeTranslations(translation, globalTranslation);

export function Row({ address, chainId, tasks }: Props): JSX.Element {
  const { t, keys } = useTranslation(mergedTranslation);
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState<boolean>(false);

  const chainData = useChainData(chainId);
  const sum = tasks
    .reduce((sum, { amountSpent }) => {
      return sum.plus(amountSpent);
    }, new BigNumber(0))
    .decimalPlaces(2)
    .toString();

  const networkName = chainData?.name || '';
  const networkIcon = chainData?.icon?.rounded || '';
  const tokenName = chainData?.tokenName || '';

  return (
    <TableRow
      data-tid={`dashboard-grouped-tasks-${address}`}
      className={classes.row}
      onClick={() => setExpanded(expanded => !expanded)}
      hover
    >
      <TableCell colSpan={3} className={classes.cell}>
        <div className={classes.cells}>
          <div className={classes.addressCell}>
            <div className={classes.iconWrapper}>
              {networkIcon && (
                <img src={networkIcon} className={classes.icon} alt="" />
              )}
            </div>

            <div className={classes.addressWithNetwork}>
              <Typography
                variant="subtitle3"
                sx={theme => ({
                  color: theme.palette.grey[900],
                  cursor: 'initial',
                })}
                onClick={event => event.stopPropagation()}
              >
                {address}
              </Typography>

              <Typography
                variant="body3"
                color={theme => theme.palette.grey[500]}
              >
                {networkName}
              </Typography>
            </div>
          </div>

          <div className={classes.tasksCount}>
            <Typography variant="subtitle3">{tasks.length}</Typography>
          </div>

          <div className={classes.sum}>
            <Typography variant="subtitle3">
              {t(keys.units.tokenAmount, {
                amount: sum,
                token: chainData?.tokenName,
              })}
            </Typography>

            <ArrowDown
              className={cx(classes.arrowIcon, {
                [classes.arrowIconRotated]: expanded,
              })}
            />
          </div>
        </div>

        <div className={classes.mobileCells}>
          <div className={classes.mobileCell}>
            <Typography
              variant="subtitle3"
              color={theme => theme.palette.grey[600]}
              mr={2}
            >
              {t(keys.name)}
            </Typography>

            <div
              className={classes.mobileAddress}
              onClick={event => event.stopPropagation()}
            >
              <div className={classes.mobileAddressWithNetwork}>
                <Typography
                  variant="subtitle3"
                  sx={theme => ({
                    color: theme.palette.grey[900],
                    cursor: 'initial',
                    wordBreak: 'break-all',
                  })}
                  onClick={event => event.stopPropagation()}
                >
                  {isMobile ? cropString(address) : address}
                </Typography>

                <Typography
                  variant="body3"
                  color={theme => theme.palette.grey[500]}
                >
                  {networkName}
                </Typography>
              </div>

              {networkIcon && (
                <img src={networkIcon} className={classes.icon} alt="" />
              )}
            </div>
          </div>

          <div className={classes.mobileCell}>
            <Typography
              variant="subtitle3"
              color={theme => theme.palette.grey[600]}
            >
              {t(keys.tasks)}
            </Typography>

            <Typography variant="subtitle3">{tasks.length}</Typography>
          </div>

          <div className={classes.mobileCell}>
            <Typography
              variant="subtitle3"
              color={theme => theme.palette.grey[600]}
            >
              {t(keys.amountSpent)}
            </Typography>

            <Typography variant="subtitle3">
              {t(keys.units.tokenAmount, {
                amount: sum,
                token: tokenName,
              })}
            </Typography>
          </div>

          <ArrowDown
            className={cx(classes.arrowIcon, {
              [classes.arrowIconRotated]: expanded,
            })}
          />
        </div>

        {expanded && (
          <div className={classes.tasks}>
            {tasks.map(task => (
              <ExpandedTask key={task.id} task={task} />
            ))}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

export const useStyles = makeStyles()(theme => ({
  row: {
    cursor: 'pointer',
  },
  cells: {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',

    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  cell: {
    '&&&': {
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(8),

      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
      },
    },
  },
  addressCell: {
    flex: 1,
    paddingRight: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(5),
  },
  addressWithNetwork: {
    display: 'flex',
    flexFlow: 'column nowrap',
    gap: theme.spacing(1.5),
  },
  tasksCount: {
    width: '20%',
    padding: theme.spacing(0, 3),
  },
  sum: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '30%',
    paddingLeft: theme.spacing(3),
    gap: theme.spacing(4),
  },
  arrowIcon: {
    color: theme.palette.grey[800],
    transform: 'rotate(0)',

    '&&': {
      transition: 'transform .15s ease',
    },

    [theme.breakpoints.down('md')]: {
      margin: '0 auto',
    },
  },
  arrowIconRotated: {
    transform: 'rotate(180deg)',
  },
  iconWrapper: {
    width: '48px',
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    width: '100%',
    height: 'auto',

    [theme.breakpoints.down('md')]: {
      width: '32px',
    },
  },
  mobileCells: {
    display: 'flex',
    flexFlow: 'column nowrap',
    gap: theme.spacing(6),

    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  mobileCell: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  mobileAddress: {
    gap: theme.spacing(5),
    display: 'flex',
    alignItems: 'center',
  },
  mobileAddressWithNetwork: {
    display: 'flex',
    flexFlow: 'column nowrap',
    gap: theme.spacing(1),
    textAlign: 'right',
  },

  tasks: {
    '&&': {
      display: 'flex',
      flexDirection: 'column',
      marginTop: theme.spacing(8),
      borderTop: `1px solid ${theme.palette.grey[200]}`,

      [theme.breakpoints.down('md')]: {
        borderTop: `none`,
        marginTop: theme.spacing(2),
      },
    },
  },
}));
